/**
 * Scene IR → three.js objects.
 *
 * Builds the architectural shell and immediate item placeholders from the IR.
 * Viewer hydrates those placeholders with cached, assembled catalog geometry.
 */
import * as THREE from 'three';
import type { Item, Level, Scene as HPScene, Surface, Vec2, Wall } from '../../core/hp/types';
import { buildWallSolids, type WallPanel, type WallSolid } from '../../core/geometry/walls';
import { wallTop, type RoomCeiling, type WallTopPiece } from '../../state/roomCeiling';
import { TextureImageCache } from './TextureImageCache';
import { bindMaterialSurface } from './MaterialTransformPreview';
import { emissiveOf, normalTextureOf, metalnessOf, opacityOf, resolveTexture, roomSurfaceOpacity, roughnessOf, specularOf, wallRoughnessOf } from '../textures';
import { holeEdgeGeometry, polygonPrismGeometry, slabGeometry } from './geometry';
import { isRegion } from '../../core/hp/regions';
import { isAttic } from '../../state/attic';
import { atticNode } from './attic';
import { wellRoom, withoutHoles, type Stairwell } from '../../core/geometry/stairwells';

export { isRegion } from '../../core/hp/regions';

export interface BuildResult {
  root: THREE.Group;
  pickable: THREE.Object3D[];
  bounds: THREE.Box3;
  stats: { walls: number; panels: number; openings: number; items: number; lights: number; triangles: number; textures: number };
  /** Geometric self-check findings; empty when the build matches the plan. */
  problems: string[];
}

const loader = new THREE.TextureLoader();
loader.crossOrigin = 'anonymous';
const defaultTextureImages = new TextureImageCache(loader);

export function textureStats(): { cached: number; failed: number } {
  return { cached: defaultTextureImages.size, failed: defaultTextureImages.failed };
}

export interface MaterialOptions {
  fallbackColor?: number;
  maxAnisotropy: number;
  side?: THREE.Side;
  /** Set for meshes whose UVs span 0..1 and whose `<Tiling>` is authoritative. */
  explicitTiling?: boolean;
  textureImages?: TextureImageCache;
  wallSide?: 'front' | 'back';
  /** A room's floor, ceiling or wall face: see-through by its colour only without a picture (`roomSurfaceOpacity`). */
  room?: boolean;
}

export function materialFor(s: Surface | undefined, opts: MaterialOptions): THREE.MeshStandardMaterial {
  const m = new THREE.MeshPhysicalMaterial({
    color: s?.color
      ? new THREE.Color().setRGB(s.color[0], s.color[1], s.color[2], THREE.SRGBColorSpace)
      : new THREE.Color(opts.fallbackColor ?? 0xf2f0ec),
    roughness: opts.wallSide ? wallRoughnessOf(s) : roughnessOf(s),
    metalness: metalnessOf(s),
    side: opts.side ?? THREE.FrontSide,
  });
  m.specularColor.setScalar(specularOf(s));
  const tex = resolveTexture(s, opts.explicitTiling);
  const images = opts.textureImages ?? defaultTextureImages;
  if (tex) {
    m.map = images.get(tex.url, { repeat: tex.repeat, offset: s?.offset, rotation: s?.rotation,
      colorSpace: THREE.SRGBColorSpace, flipY: true, anisotropy: opts.maxAnisotropy });
    m.color.set(0xffffff);   // a tinted texture would double-darken
  }
  const normal = normalTextureOf(s);
  if (normal && m.map) {
    m.normalMap = images.get(normal, { repeat: tex!.repeat, offset: s?.offset, rotation: s?.rotation,
      colorSpace: THREE.NoColorSpace, flipY: true, anisotropy: opts.maxAnisotropy });
    m.normalScale.set(.65, .65);
  }
  const opacity = opts.room ? roomSurfaceOpacity(s) : opacityOf(s);
  if (opacity !== undefined && opacity < 1) {
    m.transparent = true;
    m.opacity = opacity;
    m.depthWrite = false;
    m.side = THREE.DoubleSide;
  }
  const emissive = emissiveOf(s);
  if (emissive > 0) {
    m.emissive = m.color.clone();
    m.emissiveIntensity = emissive;
  }
  bindMaterialSurface(m, s, !!opts.explicitTiling, opts.wallSide);
  return m;
}

export interface BuildOptions {
  maxAnisotropy: number;
  showCeiling?: boolean;
  textureImages?: TextureImageCache;
  /** The floor's stairwells: where its floor and its ceilings have holes (`src/core/geometry/stairwells.ts`). */
  wells?: Wells;
  /**
   * The floor's rooms whose ceiling a mansard slopes (`slopedCeilings` of the whole floor — the shell is built without
   * its items): their walls stand only as high as the ceiling over them, their flat ceiling covers only its flat part.
   */
  ceilings?: readonly RoomCeiling[];
}

/**
 * The holes of one floor: in its floor, over the flights of the floor below that lead up to it (`wellsDown`), and in its
 * ceilings, over its own flights that lead up (`wellsUp`). `floorEdges: false` leaves the floor's holes without their
 * edge, for a floor seen from under it through a ceiling whose own hole has one.
 */
export interface Wells { floor: readonly Stairwell[]; ceiling: readonly Stairwell[]; floorEdges?: boolean }

/** How deep the edge of a hole in a floor or a ceiling is: the app's SlopeDepth (HorizontalOpeningController). */
export const WELL_EDGE_DEPTH = .15;

const holesOf = (wells: readonly Stairwell[] | undefined) => wells?.map(well => well.polygon) ?? [];

export function buildScene(hp: HPScene, opts: BuildOptions): BuildResult {
  const root = new THREE.Group();
  root.name = 'project';
  const pickable: THREE.Object3D[] = [];
  const stats = { walls: 0, panels: 0, openings: 0, items: 0, lights: 0, triangles: 0, textures: 0 };
  const level: Level | undefined = hp.levels[0];
  if (!level) return { root, pickable, bounds: new THREE.Box3(), stats, problems: ['no level in project'] };

  const aniso = opts.maxAnisotropy;
  const material = (s: Surface | undefined, options: MaterialOptions) => materialFor(s, { ...options, textureImages: opts.textureImages });
  const countTris = (g: THREE.BufferGeometry) => {
    const idx = g.getIndex();
    stats.triangles += idx ? idx.count / 3 : g.getAttribute('position').count / 3;
  };

  const slopedByRoom = new Map((opts.ceilings ?? []).map(ceiling => [ceiling.room.index, ceiling]));

  // ---- floors & ceilings ---------------------------------------------------
  for (const room of level.rooms) {
    if (room.polygon.length < 3) continue;
    const g = slabGeometry(room.polygon, 0, false, holesOf(opts.wells?.floor));
    const floor = new THREE.Mesh(g, material(room.floorSurface, { maxAnisotropy: aniso, fallbackColor: 0xd8d2c8, room: true }));
    floor.receiveShadow = true;
    floor.name = `floor:${room.index}`;
    floor.userData = { kind: 'floor', roomIndex: room.index };
    root.add(floor);
    pickable.push(floor);
    countTris(g);

    if (opts.showCeiling) {
      // Under a mansard the flat ceiling is only its flat part; the slopes are the mansards' own (`slopeNode`).
      const sloped = slopedByRoom.get(room.index);
      const cg = sloped ? flatCeilingGeometry(sloped, holesOf(opts.wells?.ceiling)) : slabGeometry(room.polygon, room.height, true, holesOf(opts.wells?.ceiling));
      const ceil = new THREE.Mesh(cg, material(room.ceilingSurface, { maxAnisotropy: aniso, fallbackColor: 0xf6f5f3, room: true }));
      ceil.material.emissive.copy(ceil.material.color);
      ceil.material.emissiveIntensity = .22; // Native ceiling bounce surrogate in the realtime preview.
      ceil.name = `ceiling:${room.index}`;
      ceil.userData = { kind: 'ceiling', roomIndex: room.index };
      root.add(ceil);
      countTris(cg);
    }
  }

  const edges = wellEdges(root, level, opts.wells);
  if (edges) { root.add(edges); edges.traverse(object => { if ((object as THREE.Mesh).isMesh) countTris((object as THREE.Mesh).geometry); }); }

  // ---- walls ---------------------------------------------------------------
  const solids: WallSolid[] = buildWallSolids(level);
  // Lite makeWall: the outer edge uses the joint of walls enlarged by two trim
  // depths. Translating each edge independently leaves overlapping corner strips.
  const enlarged = (depth: (wall: Wall) => number | undefined) => new Map(buildWallSolids({ ...level, walls: level.walls.map(wall => ({
    ...wall, thickness: wall.thickness + 2 * Math.max(depth(wall) ?? .012, .008),
  })) }).map(solid => [solid.wall.id, solid]));
  const trimSolids = enlarged(wall => wall.plinth?.depth);
  // Ceiling moldings stand out by their own depth; the joints are found again only when a wall has one.
  const moldingSolids = level.walls.some(wall => wall.molding) ? enlarged(wall => wall.molding?.depth) : trimSolids;
  for (const solid of solids) {
    stats.walls++;
    const group = new THREE.Group();
    group.name = `wall:${solid.wall.id}`;

    const inner = material(solid.wall.frontSurface, { maxAnisotropy: aniso, fallbackColor: 0xf0eeea, wallSide: 'front', room: true });
    const outer = material(solid.wall.backSurface ?? solid.wall.frontSurface, { maxAnisotropy: aniso, fallbackColor: 0xe6e3de, wallSide: 'back', room: true });
    const edge = material(solid.wall.frontSurface, { maxAnisotropy: aniso, fallbackColor: 0xe8e5e0, wallSide: 'front', room: true });
    // Prism groups are [left, right, rest]; the interior surface goes on whichever side faces the room.
    const mats = solid.interiorIsLeft ? [inner, outer, edge] : [outer, inner, edge];

    // A wall a sloped ceiling comes down on stands only as high as the ceilings beside it (`wallTop`).
    const top = slopedByRoom.size ? wallTop(level, solid.wall, slopedByRoom) : null;
    for (const panel of solid.panels) {
      const parts = top ? panelUnderTop(panel, solid.wall, top) : [{ polygon: panel.polygon, y0: panel.y0, top: panel.y1 as number | ((p: Vec2) => number) }];
      parts.forEach((part, index) => {
        stats.panels++;
        const g = polygonPrismGeometry(part.polygon, part.y0, part.top, solid.wall.start, solid.wall.end);
        const mesh = new THREE.Mesh(g, mats);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.name = `panel:${solid.wall.id}:${panel.kind}:${panel.u0.toFixed(2)}${index ? `:${index}` : ''}`;
        mesh.userData = { kind: 'wall', wallId: solid.wall.id, panel: panel.kind };
        group.add(mesh);
        pickable.push(mesh);
        countTris(g);
      });
    }

    // Skirting: a strip along the interior face of every panel that meets the floor.
    // It is interrupted by door openings, which is why it follows the panels rather
    // than the wall's full length.
    // Each trim is a group of its own inside the wall's (`plinth:<wall>`, `molding:<wall>`): picked, outlined and
    // dressed as one object, as the app's WallPlinthController is.
    const trimGroup = (kind: 'plinth' | 'molding') => { const node = new THREE.Group(); node.name = `${kind}:${solid.wall.id}`; group.add(node); return node; };
    const trim = solid.wall.plinth;
    if (trim) {
      const trimMat = material(trim.surface, { maxAnisotropy: aniso, fallbackColor: 0xf7f6f4 });
      const n = solid.wall.normalIn, holder = trimGroup('plinth');
      for (const panel of trimSolids.get(solid.wall.id)?.panels ?? []) {
        if (panel.y0 > 0.001) continue;                    // hangs above the floor
        const polygon = trimBand(panel.polygon, solid.wall.start, n, solid.wall.thickness / 2 - .0005);
        if (polygon.length < 3) continue;
        const g = polygonPrismGeometry(polygon, -.002, Math.min(trim.height, panel.y1), solid.wall.start, solid.wall.end);
        const mesh = new THREE.Mesh(g, trimMat);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData = { kind: 'plinth', wallId: solid.wall.id };
        holder.add(mesh);
        pickable.push(mesh);
        countTris(g);
      }
    }

    // Ceiling molding (the app's CeiliningMolding, model 998): a strip along the top of the interior face, on every
    // panel that reaches it — an opening up to the top interrupts it, as a door interrupts the plinth.
    const molding = solid.wall.molding;
    if (molding) {
      const moldingMat = material(molding.surface, { maxAnisotropy: aniso, fallbackColor: 0xf7f6f4 });
      const n = solid.wall.normalIn, top = solid.wall.height, holder = trimGroup('molding');
      for (const panel of moldingSolids.get(solid.wall.id)?.panels ?? []) {
        if (panel.y1 < top - 0.001) continue;               // stops below the top
        const polygon = trimBand(panel.polygon, solid.wall.start, n, solid.wall.thickness / 2 - .0005);
        if (polygon.length < 3) continue;
        const g = polygonPrismGeometry(polygon, Math.max(panel.y0, top - molding.height), top + .002, solid.wall.start, solid.wall.end);
        const mesh = new THREE.Mesh(g, moldingMat);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData = { kind: 'molding', wallId: solid.wall.id };
        holder.add(mesh);
        pickable.push(mesh);
        countTris(g);
      }
    }

    // Closed panels already supply jambs, sill and head; separate reveal meshes would overlap them.
    stats.openings += solid.reveals.length;

    // Exterior views use the shared native sky/ground panorama (E8).

    root.add(group);
  }

  // ---- items ---------------------------------------------------------------
  const itemGroup = new THREE.Group();
  itemGroup.name = 'items';
  for (const item of level.items) {
    // A mansard is built as it stands at once: its wedge has no model to wait for (src/render/three/attic.ts).
    const mesh = isAttic(item) ? atticNode(item, level, { maxAnisotropy: aniso, textureImages: opts.textureImages }) : placeholderFor(item, aniso, opts.textureImages);
    itemGroup.add(mesh);
    pickable.push(mesh);
    stats.items++;
    stats.triangles += 12;
    if (item.light) {
      const l = new THREE.PointLight(
        new THREE.Color().setRGB(item.light.color[0], item.light.color[1], item.light.color[2], THREE.SRGBColorSpace),
        item.light.intensity * 3,
        item.light.range,
        2,
      );
      l.position.set(item.position.x, item.elevation + item.size.h / 2, item.position.z);
      l.name = `light:${item.id}`;
      itemGroup.add(l);
      stats.lights++;
    }
  }
  root.add(itemGroup);

  stats.textures = (opts.textureImages ?? defaultTextureImages).size;
  // Exterior photos never participate in architectural camera framing.
  const bounds = new THREE.Box3().makeEmpty();
  for (const child of root.children) {
    if (child.userData?.kind === 'view') continue;
    bounds.expandByObject(child);
  }
  const problems = auditScene(hp, root);
  if (problems.length) console.warn('[hp:scene] geometry audit:\n' + problems.join('\n'));
  return { root, pickable, bounds, stats, problems };
}

/**
 * The edges of a floor's stairwells, in the finish of the floor or the ceiling each hole is cut in: a strip down from the
 * floor round each hole in it, and up from the ceiling round each hole in that, as the app gives its floor and ceiling
 * openings slopes. Null when there is none.
 */
function wellEdges(root: THREE.Object3D, level: Level, wells: Wells | undefined): THREE.Group | null {
  if (!wells || (!wells.floor.length && !wells.ceiling.length)) return null;
  const group = new THREE.Group();
  group.name = 'stairwells';
  group.userData = { kind: 'stairwell' };
  const edge = (well: Stairwell, surface: 'floor' | 'ceiling') => {
    const room = wellRoom(level.rooms, well);
    const slab = room && root.getObjectByName(`${surface}:${room.index}`) as THREE.Mesh | undefined;
    if (!room || !slab) return;
    const y = surface === 'floor' ? 0 : room.height;
    const mesh = new THREE.Mesh(holeEdgeGeometry(well.polygon, y, surface === 'floor' ? y - WELL_EDGE_DEPTH : y + WELL_EDGE_DEPTH), slab.material);
    mesh.name = `stairwell:${surface}:${well.id}`;
    mesh.userData = { kind: 'stairwell' };
    mesh.castShadow = true; mesh.receiveShadow = true;
    group.add(mesh);
  };
  if (wells.floorEdges !== false) for (const well of wells.floor) edge(well, 'floor');
  for (const well of wells.ceiling) edge(well, 'ceiling');
  return group.children.length ? group : null;
}

/**
 * Cuts `wells` into the floors and ceilings `root` already holds for `level`'s rooms and gives the holes their edges,
 * the rest of the shell left as it is: the stairwells change with the flights, which a shell is not built again for.
 */
export function applyWells(root: THREE.Object3D, level: Level, wells: Wells | undefined): void {
  for (const room of level.rooms) {
    if (room.polygon.length < 3) continue;
    const floor = root.getObjectByName(`floor:${room.index}`) as THREE.Mesh | undefined;
    if (floor?.isMesh) { floor.geometry.dispose(); floor.geometry = slabGeometry(room.polygon, 0, false, holesOf(wells?.floor)); }
    const ceiling = root.getObjectByName(`ceiling:${room.index}`) as THREE.Mesh | undefined;
    if (ceiling?.isMesh) { ceiling.geometry.dispose(); ceiling.geometry = slabGeometry(room.polygon, room.height, true, holesOf(wells?.ceiling)); }
  }
  const old = root.getObjectByName('stairwells');
  if (old) { old.removeFromParent(); old.traverse(object => (object as THREE.Mesh).geometry?.dispose()); }
  const edges = wellEdges(root, level, wells);
  if (edges) root.add(edges);
}

/** Keep the interior band of the expanded native wall joint. */
function trimBand(polygon: Vec2[], origin: Vec2, normal: Vec2, distance: number): Vec2[] {
  const result: Vec2[] = [];
  const signed = (p: Vec2) => (p.x - origin.x) * normal.x + (p.z - origin.z) * normal.z - distance;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i], b = polygon[(i + 1) % polygon.length], da = signed(a), db = signed(b);
    if (da >= 0) result.push(a);
    if ((da >= 0) !== (db >= 0)) {
      const t = da / (da - db);
      result.push({ x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t });
    }
  }
  return result;
}

/**
 * Cheap geometric self-check, run on every build.
 *
 * A 3D screenshot hides mirrored, offset and doubled geometry — every one of those is
 * a wrong NUMBER first, so they are caught here by comparing what got built against
 * what the IR says, rather than by looking at a picture.
 */
function auditScene(hp: HPScene, root: THREE.Group): string[] {
  const out: string[] = [];
  const level = hp.levels[0];
  if (!level) return out;
  const box = new THREE.Box3();
  const tmp = new THREE.Box3();

  const irBounds = (pts: Array<{ x: number; z: number }>) => {
    const xs = pts.map((p) => p.x); const zs = pts.map((p) => p.z);
    return { x0: Math.min(...xs), x1: Math.max(...xs), z0: Math.min(...zs), z1: Math.max(...zs) };
  };
  const check = (label: string, got: THREE.Box3, want: { x0: number; x1: number; z0: number; z1: number }, tol: number) => {
    const d = [
      Math.abs(got.min.x - want.x0), Math.abs(got.max.x - want.x1),
      Math.abs(got.min.z - want.z0), Math.abs(got.max.z - want.z1),
    ];
    if (Math.max(...d) > tol) {
      out.push(`  ${label}: built [${got.min.x.toFixed(2)}..${got.max.x.toFixed(2)}] x `
        + `[${got.min.z.toFixed(2)}..${got.max.z.toFixed(2)}] but the plan says `
        + `[${want.x0.toFixed(2)}..${want.x1.toFixed(2)}] x [${want.z0.toFixed(2)}..${want.z1.toFixed(2)}]`);
    }
  };

  for (const room of level.rooms) {
    const mesh = root.getObjectByName(`floor:${room.index}`);
    if (!mesh) { out.push(`  floor ${room.index}: not built`); continue; }
    // A stairwell can take a whole side off a floor.
    if (root.getObjectByName('stairwells')) continue;
    check(`floor ${room.index}`, box.setFromObject(mesh), irBounds(room.polygon), 0.01);
  }

  // Walls: the union of the built panels must cover the centreline extent.
  const wallPts = level.walls.flatMap((w) => [w.start, w.end]);
  if (wallPts.length) {
    const group = new THREE.Box3().makeEmpty();
    for (const w of level.walls) {
      const g = root.getObjectByName(`wall:${w.id}`);
      if (g) group.union(tmp.setFromObject(g));
    }
    const want = irBounds(wallPts);
    const t = Math.max(...level.walls.map((w) => w.thickness)) / 2 + 0.02;
    if (group.min.x < want.x0 - t - 0.01 || group.max.x > want.x1 + t + 0.01
      || group.min.z < want.z0 - t - 0.01 || group.max.z > want.z1 + t + 0.01) {
      out.push(`  walls: built [${group.min.x.toFixed(2)}..${group.max.x.toFixed(2)}] x `
        + `[${group.min.z.toFixed(2)}..${group.max.z.toFixed(2)}] overflows the centrelines `
        + `[${want.x0.toFixed(2)}..${want.x1.toFixed(2)}] x [${want.z0.toFixed(2)}..${want.z1.toFixed(2)}] by more than half a wall`);
    }
    const height = level.walls[0].height;
    if (Math.abs(group.max.y - height) > 0.02) out.push(`  walls: top at ${group.max.y.toFixed(3)} m, expected ${height} m`);
    if (Math.abs(group.min.y) > 0.02) out.push(`  walls: base at ${group.min.y.toFixed(3)} m, expected 0`);
  }

  // Items must sit inside the room they belong to, and above the floor.
  const poly = level.rooms[0]?.polygon;
  if (poly && poly.length >= 3) {
    let outside = 0;
    let sunken = 0;
    for (const it of level.items) {
      if (!pointInPolygon(poly, it.position)) outside++;
      if (it.elevation < -0.01) sunken++;
    }
    if (outside) out.push(`  items: ${outside}/${level.items.length} fall outside the room outline`);
    if (sunken) out.push(`  items: ${sunken} sit below the floor`);
  }
  return out;
}

function pointInPolygon(poly: Array<{ x: number; z: number }>, p: { x: number; z: number }): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]; const b = poly[j];
    if ((a.z > p.z) !== (b.z > p.z) && p.x < ((b.x - a.x) * (p.z - a.z)) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}


const REGION_COLOR: Record<Item['placement'], number> = { floor: 0xc8b89a, wall: 0xb7a1c9, ceiling: 0x9fc4b6 };

/** What a placeholder box says about its model: still coming, or not coming until someone acts. */
export type PlaceholderState = 'pending' | 'failed';

/** The look of a placeholder box per state: grey while the model is on its way, amber when it is not. */
export const PLACEHOLDER_LOOK: Record<PlaceholderState, { color: number; opacity: number }> = {
  pending: { color: 0xaaaaaa, opacity: .24 },
  failed: { color: 0xc27c2c, opacity: .32 },
};

export function paintPlaceholder(mesh: THREE.Mesh, state: PlaceholderState): void {
  const material = mesh.material as THREE.MeshStandardMaterial;
  if (!material?.isMeshStandardMaterial || mesh.userData.generated) return;
  const look = PLACEHOLDER_LOOK[state];
  material.color.setHex(look.color); material.opacity = look.opacity; material.needsUpdate = true;
  mesh.userData.placeholderState = state;
}

/** Region boxes are final architectural surfaces; other boxes only mark pending geometry. */
export function placeholderFor(item: Item, aniso: number, textureImages?: TextureImageCache, state: PlaceholderState = 'pending'): THREE.Mesh {
  const procedural = isRegion(item);
  const primary = item.materials ? Object.values(item.materials)[0] : undefined;
  const look = PLACEHOLDER_LOOK[state];
  const mat = procedural ? materialFor(primary, {
    textureImages,
    maxAnisotropy: aniso,
    fallbackColor: REGION_COLOR[item.placement],
    explicitTiling: item.usesExplicitTiling,
  }) : new THREE.MeshStandardMaterial({ color: look.color, roughness: 1, transparent: true, opacity: look.opacity, depthWrite: false });
  const geo = new THREE.BoxGeometry(item.size.w, item.size.h, item.size.d);
  const mesh = new THREE.Mesh(geo, mat);
  // `elevation` is where the item's bottom sits; a box is centred on its own origin.
  mesh.position.set(item.position.x, item.elevation + item.size.h / 2, item.position.z);
  // `item.rotation` is the reference implementation's yaw, whose forward is
  // (−sin r, cos r). three.js turns +Z by Ry(a) into (+sin a, cos a), so the angle has
  // to be negated. Measured on the target project: 23/23 wall items face the room this
  // way, 0/23 without the sign — and a wrong sign is invisible on a symmetric box.
  mesh.rotation.y = -item.rotation;
  if (item.mirrored) mesh.scale.x = -1;
  const dims = [item.size.w, item.size.h, item.size.d].sort((a, b) => b - a);
  mesh.castShadow = procedural && dims[1] >= 0.32;
  mesh.receiveShadow = procedural;
  mesh.name = `item:${item.id}`;
  mesh.userData = { kind: 'item', itemId: item.id, caption: item.caption, model: item.modelRef.raw, placeholderState: procedural ? undefined : state };
  return mesh;
}

/** The part of a polygon where `f` is ≥ 0, `f` linear over the plan. */
function clipLinear(polygon: readonly Vec2[], f: (p: Vec2) => number): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < polygon.length; i++) {
    const p = polygon[i], q = polygon[(i + 1) % polygon.length], fp = f(p), fq = f(q);
    if (fp >= -1e-9) out.push(p);
    if ((fp > 1e-9 && fq < -1e-9) || (fp < -1e-9 && fq > 1e-9)) { const t = fp / (fp - fq); out.push({ x: p.x + (q.x - p.x) * t, z: p.z + (q.z - p.z) * t }); }
  }
  return out;
}

/**
 * The pieces of a wall panel under the wall's top where a sloped ceiling brings it down (`wallTop`): split where the top
 * bends, where it crosses the panel's own top or its bottom; each with its top as a plane rising along the wall. What
 * the top leaves of a panel over a window — or of all of it — is nothing. The top keeps its corner height inside the
 * walls the panel's ends meet.
 */
export function panelUnderTop(panel: Pick<WallPanel, 'polygon' | 'y0' | 'y1'>, wall: Pick<Wall, 'start' | 'dir' | 'length'>, pieces: readonly WallTopPiece[]):
  Array<{ polygon: Vec2[]; y0: number; top: (p: Vec2) => number }> {
  const along = (p: Vec2) => (p.x - wall.start.x) * wall.dir.x + (p.z - wall.start.z) * wall.dir.z;
  const us = panel.polygon.map(along), lo = Math.min(...us), hi = Math.max(...us);
  const sorted = [...pieces].sort((a, b) => a.u0 - b.u0);
  if (!sorted.length) return [{ polygon: [...panel.polygon], y0: panel.y0, top: () => panel.y1 }];
  // The top as stretches over the whole line of the wall: before its first piece and after its last, level.
  const first = sorted[0], last = sorted[sorted.length - 1];
  const stretches = [{ u0: -Infinity, u1: first.u0, h0: first.h0, h1: first.h0 }, ...sorted, { u0: last.u1, u1: Infinity, h0: last.h1, h1: last.h1 }];
  const out: Array<{ polygon: Vec2[]; y0: number; top: (p: Vec2) => number }> = [];
  for (const stretch of stretches) {
    const a = Math.max(lo, stretch.u0), b = Math.min(hi, stretch.u1);
    if (b - a < 1e-7) continue;
    const span = Number.isFinite(stretch.u1 - stretch.u0) ? stretch.u1 - stretch.u0 : 0;
    const height = (u: number) => span > 1e-12 ? stretch.h0 + (stretch.h1 - stretch.h0) * (u - stretch.u0) / span : stretch.h0;
    const capped = (u: number) => Math.min(panel.y1, height(u));
    // Split where the stretch crosses the panel's own top, then where what is left crosses its bottom.
    const marks = [a, b];
    for (const level of [panel.y1, panel.y0]) {
      const ha = height(a), hb = height(b);
      if ((ha - level) * (hb - level) < 0) marks.push(a + (level - ha) / (hb - ha) * (b - a));
    }
    marks.sort((p, q) => p - q);
    for (let i = 0; i + 1 < marks.length; i++) {
      const c = marks[i], d = marks[i + 1];
      if (d - c < 1e-7) continue;
      const tc = capped(c), td = capped(d);
      if (Math.max(tc, td) <= panel.y0 + 1e-4) continue;
      let polygon = clipLinear(panel.polygon, p => along(p) - c);
      polygon = clipLinear(polygon, p => d - along(p));
      if (polygon.length < 3) continue;
      const top = (p: Vec2) => Math.max(panel.y0, tc + (td - tc) * (along(p) - c) / (d - c));
      out.push({ polygon, y0: panel.y0, top });
    }
  }
  return out;
}

/** A room's flat ceiling under a mansard: its cells under the flat ceiling, facing down, less the stairwells. */
function flatCeilingGeometry(ceiling: RoomCeiling, holes: readonly (readonly Vec2[])[]): THREE.BufferGeometry {
  const pieces = ceiling.cells.filter(cell => cell.surface.kind === 'flat').flatMap(cell => withoutHoles(cell.polygon, holes));
  const shapes = pieces.map(piece => new THREE.Shape(piece.map(p => new THREE.Vector2(p.x, -p.z))));
  const g = new THREE.ShapeGeometry(shapes);
  g.rotateX(-Math.PI / 2);
  const index = g.getIndex();
  if (index) { const a = index.array as unknown as number[]; for (let i = 0; i < a.length; i += 3) { const t = a[i]; a[i] = a[i + 2]; a[i + 2] = t; } index.needsUpdate = true; }
  g.computeVertexNormals();
  g.translate(0, ceiling.height, 0);
  return g;
}
